/**
 * Lese-Hooks für Standorte und Vorlagen aus dem lokalen Cache.
 *
 * Mutations für Standorte/Vorlagen sind admin-only und werden nicht offline
 * unterstützt — diese Daten ändern sich selten und brauchen Server-Bestätigung
 * (Konflikt-Risiko sonst hoch). Schreib-Operationen laufen weiterhin direkt
 * über die `locationsApi`/`templatesApi`-Clients.
 */

import { useLiveQuery } from 'dexie-react-hooks';
import { db, type LocalLocation, type LocalTemplate } from '../db/schema';

export interface LocalLocationWithTemplates extends LocalLocation {
  templates: LocalTemplate[];
}

/** Liefert alle Standorte. */
export function useLocations(): LocalLocation[] | undefined {
  return useLiveQuery(() => db.locations.toArray(), [], []);
}

/** Liefert Standorte inkl. ihrer (aktiven) Vorlagen, fertig zum Auswählen. */
export function useLocationsWithTemplates(): LocalLocationWithTemplates[] | undefined {
  return useLiveQuery(async () => {
    const [locs, tpls] = await Promise.all([db.locations.toArray(), db.templates.toArray()]);
    return locs.map((l) => ({
      ...l,
      templates: tpls.filter((t) => t.locationId === l.id),
    }));
  }, []);
}

/** Standort per ID. */
export function useLocationById(id: string | undefined): LocalLocation | undefined {
  return useLiveQuery<LocalLocation | undefined>(
    async () => (id ? await db.locations.get(id) : undefined),
    [id],
  );
}

/** Map id → Standort, praktisch zum Auflösen in Listen. */
export function useLocationsMap(): Map<string, LocalLocation> {
  const list = useLocations();
  const map = new Map<string, LocalLocation>();
  for (const l of list ?? []) map.set(l.id, l);
  return map;
}
